// src/App.js
import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WalletProvider } from './economy/WalletContext';
import Header from './components/Header';
import Footer from './components/Footer';
import { CookieConsent, PWAInstallPrompt } from './components/Overlays';
import HomePage from './pages/HomePage';
import './App.css';

const GamePage = lazy(() => import('./pages/GamePage'));
const GuidePage = lazy(() => import('./pages/GuidePage'));
const GuidesIndexPage = lazy(() => import('./pages/GuidesIndexPage'));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const AboutPage = lazy(() => import('./pages/InfoPages').then((m) => ({ default: m.AboutPage })));
const ContactPage = lazy(() => import('./pages/InfoPages').then((m) => ({ default: m.ContactPage })));
const PrivacyPage = lazy(() => import('./pages/InfoPages').then((m) => ({ default: m.PrivacyPage })));
const ProfileModal = lazy(() => import('./components/ProfileModal'));
const SiteNotices = lazy(() => import('./components/SiteNotices'));
const FriendsPage = lazy(() => import('./pages/FriendsPage'));
const DevOnlinePreview = process.env.NODE_ENV === 'development' ? lazy(() => import('./multiplayer/DevOnlinePreview')) : null;

/** Sayfa değişince başa kaydır; #çapa varsa ona git. */
function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (el) {
        el.scrollIntoView();
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function LegacyLeaderboard() {
  const { scope } = useParams();
  return <Navigate to={!scope || scope === 'general' ? '/lider-tablosu' : `/lider-tablosu/${scope}`} replace />;
}

function PageFallback() {
  return (
    <div className="container py-5" aria-busy="true">
      <span className="skeleton mb-3" style={{ width: '40%', height: 28 }} />
      <span className="skeleton mb-2" />
      <span className="skeleton" style={{ width: '70%' }} />
    </div>
  );
}

function useIdle(delay = 2000) {
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    const done = () => setIdle(true);
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(done, { timeout: delay + 2000 });
      return () => window.cancelIdleCallback(id);
    }
    const t = setTimeout(done, delay);
    return () => clearTimeout(t);
  }, [delay]);
  return idle;
}

function Shell() {
  const { user } = useAuth();
  const [profileOpen, setProfileOpen] = useState(false);
  const idle = useIdle();
  const pushAvailable = typeof Notification !== 'undefined' && Notification.permission === 'default' && !!user;

  // Bildirim izni zaten verilmişse token'ı tazele; ön plandaki bildirimleri dinle
  useEffect(() => {
    if (!user || typeof Notification === 'undefined' || Notification.permission !== 'granted') return undefined;
    let unsub = () => {};
    let alive = true;
    Promise.all([import('./firebase'), import('./firebaseHelpers')]).then(async ([fb, helpers]) => {
      const token = await fb.requestFcmToken();
      if (token && alive) helpers.saveFcmToken(user.uid, token).catch(() => {});
      unsub = await fb.onForegroundFcm((payload) => {
        const n = payload?.notification || {};
        if (n.title && document.visibilityState === 'visible' && 'serviceWorker' in navigator) {
          navigator.serviceWorker.ready.then((reg) => reg.showNotification(n.title, { body: n.body, icon: '/img/icons/icon-192.png', data: payload.data || {} }));
        }
      });
    });
    return () => {
      alive = false;
      unsub();
    };
  }, [user]);

  const enablePush = useCallback(async () => {
    if (!user) return;
    const [fb, helpers] = await Promise.all([import('./firebase'), import('./firebaseHelpers')]);
    const token = await fb.requestFcmToken();
    if (token) {
      await helpers.saveFcmToken(user.uid, token);
      alert('Bildirimler açıldı.');
    } else {
      alert('Bildirim izni verilmedi ya da tarayıcınız desteklemiyor.');
    }
  }, [user]);

  return (
    <div className="app-shell">
      <a className="skip-link" href="#icerik">İçeriğe geç</a>
      <ScrollManager />
      <Header onOpenProfile={() => setProfileOpen(true)} onEnablePush={enablePush} pushAvailable={pushAvailable} />
      <main id="icerik" className="page-content">
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/oyun/:gameId" element={<GamePage />} />
            <Route path="/rehber" element={<GuidesIndexPage />} />
            <Route path="/rehber/:guideId" element={<GuidePage />} />
            <Route path="/lider-tablosu" element={<LeaderboardPage />} />
            <Route path="/lider-tablosu/:scope" element={<LeaderboardPage />} />
            <Route path="/leaderboards" element={<LegacyLeaderboard />} />
            <Route path="/leaderboards/:scope" element={<LegacyLeaderboard />} />
            <Route path="/profil/:nickname" element={<ProfilePage />} />
            <Route path="/arkadaslar" element={<FriendsPage />} />
            <Route path="/oyuntu/hakkimizda" element={<AboutPage />} />
            <Route path="/oyuntu/iletisim" element={<ContactPage />} />
            <Route path="/oyuntu/gizlilik" element={<PrivacyPage />} />
            {DevOnlinePreview && <Route path="/__online/:game" element={<DevOnlinePreview />} />}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <CookieConsent />
      <PWAInstallPrompt />
      <Suspense fallback={null}>
        {profileOpen && <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />}
        {idle && <SiteNotices />}
      </Suspense>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <WalletProvider>
          <Shell />
        </WalletProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
